import { HIMediaDTO, HIMediaWriteDTO } from './hi-media';
import { ExpressionEnum } from './link-expression';

import { z } from 'zod/v4';
import { SeverityDTO } from './severity';

export const TQLHILinkTypeDTO = z.enum([
  'raw',
  'calculate',
  'event',
  'alert_hint',
  'event_hint',
  'static',
]);
export type TQLHILinkType = z.infer<typeof TQLHILinkTypeDTO>;

const VMAlertForTQLHILinkDTO = z.object({
  name: z.string(),
  label: z.string(),
});

/** HILinkDependCreateSchema */
export const HILinkDependCreateDTO = z.object({
  hi_link_id: z.uuid(),
  link_id: z.uuid(),
});
export type HILinkDependCreate = z.infer<typeof HILinkDependCreateDTO>;

/** HILinkDependInfoSchema */
export const HILinkDependInfoDTO = z.object({
  hi_link_id: z.uuid(),
  hi_link_name: z.string().optional(),
  link_id: z.uuid().optional(),
  template_name: z.string().optional(),
});
export type HILinkDependInfo = z.infer<typeof HILinkDependInfoDTO>;

/** HILinkDependUpdateSchema */
export const HILinkDependUpdateDTO = z.object({
  hi_link_id: z.uuid(),
  link_id: z.uuid(),
});
export type HILinkDependUpdate = z.infer<typeof HILinkDependUpdateDTO>;

/** HIParsersInterface */
export const HIParsersDTO = z.object({
  '2': z.string(),
  '3': z.string(),
  '4': z.string(),
  '5': z.string(),
});
export type HIParsers = z.infer<typeof HIParsersDTO>;

/** HiLinkResponseArguments */
export const HILinkArgumentsDTO = z.object({
  percentile: z.number().optional(),
  field: z.string().optional(),
  parsers: HIParsersDTO.optional(),
  severity_mapping_policy: z.record(z.string(), SeverityDTO).optional(),
});
export type HiLinkResponseArguments = z.infer<typeof HILinkArgumentsDTO>;

/** HILinkChangeRequest */
export const HILinkChangeRequestDTO = z.object({
  metric_id: z.uuid().optional(),
  rule: z.string().optional(),
  type: TQLHILinkTypeDTO.optional(),
  expression: z.nativeEnum(ExpressionEnum).optional(),
  arguments: z.record(z.string(), z.any()).optional(),
  depend_hi_link_ids: z.array(HILinkDependUpdateDTO).optional(),
  ci_filter: z.boolean().optional(),
  filter: z.record(z.string(), z.any()).optional(),
  query: z.record(z.string(), z.any()).optional(),
  event_path: z.string().optional(),
});
export type HILinkChangeRequest = z.infer<typeof HILinkChangeRequestDTO>;

/** HILinkCreateRequest */
export const HILinkCreateRequestDTO = z.object({
  hi_type_id: z.uuid(),
  ci_type_id: z.uuid(),
  metric_id: z.uuid().optional(),
  rule: z.string().optional(),
  type: TQLHILinkTypeDTO,
  expression: z.nativeEnum(ExpressionEnum).optional(),
  arguments: z.record(z.string(), z.any()).optional(),
  depend_hi_link_ids: z.array(HILinkDependCreateDTO).optional(),
  ci_filter: z.boolean().optional(),
  filter: z.record(z.string(), z.any()).optional(),
  query: z.record(z.string(), z.any()).optional(),
  event_path: z.string().optional(),
});
export type HILinkCreateRequest = z.infer<typeof HILinkCreateRequestDTO>;

/** HILinkInfoResponse */
export const HILinkInfoDTO = z.object({
  id: z.uuid(),
  hi_type_id: z.uuid(),
  hi_type_label: z.string(),
  ci_type_id: z.uuid(),
  rule: z.string(),
  type: TQLHILinkTypeDTO,
  metric_name: z.string().optional(),
  metric_id: z.uuid().optional(),
  hi_type_name: z.string(),
  expression: z.nativeEnum(ExpressionEnum).optional(),
  arguments: HILinkArgumentsDTO,
  enabled: z.boolean(),
  depend_hi_link_ids: z.array(HILinkDependInfoDTO).optional(),
  ci_filter: z.boolean(),
  query: z.record(z.string(), z.any()).optional(),
  filter: z.record(z.string(), z.any()).optional(),
});
export type HILinkInfo = z.infer<typeof HILinkInfoDTO>;

/** Directed rules of a monitoring template. Legacy rules omit tql_edge_id. */
export const TQLHILinkDependDTO = z.object({
  hi_link_id: z.uuid().nullable(),
  link_id: z.uuid(),
  src_node_id: z.string(),
  hi_type_name: z.string(),
  tql_edge_id: z.string().min(1).nullish(),
});
export type TQLHILinkDepend = z.infer<typeof TQLHILinkDependDTO>;

export const TQLHILinkDependInfoDTO = TQLHILinkDependDTO.omit({
  src_node_id: true,
}).extend({
  hi_type_label: z.string().optional(),
  template_name: z.string().nullish(),
  origin_node_id: z.string().nullish(),
});
export type TQLHILinkDependInfo = z.infer<typeof TQLHILinkDependInfoDTO>;

/** TQLHILinkInfoResponse */
export const TQLHILinkInfoDTO = z.object({
  id: z.uuid(),
  template_name: z.string(),
  rule: z.string().optional(),
  type: TQLHILinkTypeDTO,
  metric_name: z.string().optional(),
  hi_type_name: z.string(),
  hi_type_label: z.string().optional(),
  template_label: z.string().optional(),
  vmalerts: z.array(VMAlertForTQLHILinkDTO).optional().nullable(),
  vmalert_names: z.array(z.string()).optional(),
  expression: z.nativeEnum(ExpressionEnum).optional(),
  arguments: z.any().optional(),
  enabled: z.boolean(),
  depend_hi_link_ids: z.array(TQLHILinkDependDTO).optional(),
  depends_with_name: z
    .record(z.string(), z.array(TQLHILinkDependInfoDTO))
    .optional(),
  event_path: z.string().optional(),
  node_id: z.union([z.string(), z.number()]).optional().nullable(),
  his_count: z.number().optional(),
});
export type TQLHILinkInfo = z.infer<typeof TQLHILinkInfoDTO>;

/** TQLHIAlertHintResponseDTO */
export const TQLHIAlertHintDTO = z.object({
  id: z.uuid(),
  hi_type_name: z.string(),
  hi_type_label: z.string(),
  template_name: z.string(),
  template_label: z.string(),
  rule: z.string(),
  ci_hint_rule: z.string(),
  media: z.array(HIMediaDTO).optional(),
  type: TQLHILinkTypeDTO.default('alert_hint'),
  arguments: z.tuple([z.number()]),
  enabled: z.boolean(),
  node_id: z.string(), // was string in your interface
  vmalerts: z.array(VMAlertForTQLHILinkDTO).optional().nullable(),
  vmalert_names: z.array(z.string()).optional(),
});
export type TQLHIAlertHint = z.infer<typeof TQLHIAlertHintDTO>;

export const TQLHIAlertHintCreateDTO = TQLHIAlertHintDTO.pick({
  rule: true,
  ci_hint_rule: true,
  arguments: true,
  node_id: true,
  hi_type_name: true,
  template_name: true,
  vmalert_names: true,
}).extend({
  type: TQLHILinkTypeDTO,
  media: z.array(HIMediaWriteDTO).max(1).nullish(),
});
export type TQLHIAlertHintCreate = z.infer<typeof TQLHIAlertHintCreateDTO>;

/** TQLHIStaticResponseDTO */
export const TQLHIStaticDTO = z.object({
  id: z.uuid(),
  hi_type_name: z.string(),
  hi_type_label: z.string(),
  template_name: z.string(),
  template_label: z.string(),
  type: TQLHILinkTypeDTO.default('static'),
  arguments: z.array(z.any()),
  enabled: z.boolean(),
  node_id: z.string().nullable().optional(),
});
export type TQLHIStatic = z.infer<typeof TQLHIStaticDTO>;

export const TQLHIStaticCreateDTO = z.object({
  arguments: z.array(z.any()),
  node_id: z.string(),
  hi_type_name: z.string(),
  template_name: z.string(),
});
export type TQLHIStaticCreate = z.infer<typeof TQLHIStaticCreateDTO>;

export const TQLHIStaticUpdateDTO = TQLHIStaticCreateDTO.omit({
  template_name: true,
});
export type TQLHIStaticUpdate = z.infer<typeof TQLHIStaticUpdateDTO>;

/** TQLHIEventHintResponseDTO */
export const TQLHIEventHintDTO = z.object({
  id: z.uuid(),
  hi_type_name: z.string(),
  hi_type_label: z.string(),
  template_name: z.string(),
  template_label: z.string(),
  ci_hint_rule: z.string(),
  hi_hint_rule: z.string(),
  type: TQLHILinkTypeDTO.default('event_hint'),
  arguments: z.array(z.any()),
  enabled: z.boolean(),
  node_id: z.string().nullable().optional(),
});
export type TQLHIEventHint = z.infer<typeof TQLHIEventHintDTO>;

export const TQLHIEventHintCreateDTO = z.object({
  ci_hint_rule: z.string(),
  hi_hint_rule: z.string(),
  arguments: z.array(z.any()),
  node_id: z.string(),
  hi_type_name: z.string(),
  template_name: z.string(),
});
export type TQLHIEventHintCreate = z.infer<typeof TQLHIEventHintCreateDTO>;

export const TQLHIEventHintUpdateDTO = TQLHIEventHintCreateDTO.omit({
  template_name: true,
});
export type TQLHIEventHintUpdate = z.infer<typeof TQLHIEventHintUpdateDTO>;
